const sequenceCounters: Record<string, number> = {};

export const simulateDelay = async (ms = 500): Promise<void> => {
  await new Promise((resolve) => setTimeout(resolve, ms));
};

export const nextSequenceValue = (namespace: string): number => {
  sequenceCounters[namespace] = (sequenceCounters[namespace] ?? 0) + 1;
  return sequenceCounters[namespace];
};

type PrefixedIdOptions = {
  pad?: number;
  radix?: number;
  uppercase?: boolean;
};

export const nextPrefixedId = (
  namespace: string,
  prefix: string,
  { pad = 4, radix = 10, uppercase = false }: PrefixedIdOptions = {},
): string => {
  if (radix < 2 || radix > 36) {
    throw new RangeError("radix must be between 2 and 36");
  }

  const sequence = nextSequenceValue(namespace);
  let suffix = sequence.toString(radix);
  suffix = suffix.padStart(pad, "0");
  if (uppercase) {
    suffix = suffix.toUpperCase();
  }
  return `${prefix}${suffix}`;
};

const RAW_READABLE_CHARACTERS =
  "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789";
const AMBIGUOUS_CHARACTERS = new Set(["I", "l", "i", "1", "o", "O", "0"]);

export const READABLE_ID_CHARACTERS = RAW_READABLE_CHARACTERS.split("")
  .filter((char) => !AMBIGUOUS_CHARACTERS.has(char))
  .join("");

const READABLE_ID_BASE = READABLE_ID_CHARACTERS.length;
const READABLE_ID_PAD_CHAR = READABLE_ID_CHARACTERS[0];

export const encodeReadableId = (value: number, length: number): string => {
  if (value < 0) {
    throw new RangeError("value must be a non-negative integer");
  }
  if (length <= 0) {
    throw new RangeError("length must be a positive integer");
  }

  let current = value;
  let result = "";

  do {
    const remainder = current % READABLE_ID_BASE;
    result = `${READABLE_ID_CHARACTERS[remainder]}${result}`;
    current = Math.floor(current / READABLE_ID_BASE);
  } while (current > 0);

  if (result.length > length) {
    return result.slice(-length);
  }

  return result.padStart(length, READABLE_ID_PAD_CHAR);
};

export const nextReadableId = (namespace: string, length: number): string => {
  const sequence = nextSequenceValue(namespace) - 1;
  return encodeReadableId(sequence, length);
};
